import MiniSearch from 'minisearch'
import { tokenize, tokenizeQuery } from './textNormalize'
import { applyAlgoliaRerank } from './algoliaComparator'
import { setQuestionBodies, setQuestionChoices, questionChoiceBodies } from './documentFields'

// Build memoised MiniSearch indices from Redux-derived collections.
//
// MiniSearch is a small in-memory full-text engine that uses BM25 scoring
// over a configurable per-token analyser. It is the content search engine:
// Algolia was retired once the tuned profiles matched it closely enough, and
// the profile registry below is what production and every experiment run on.
//
// There is one MiniSearch instance per result index — `repos`, `folders`,
// `sets_questions` — so the UI primitives (`IndexHitList` / `SectionHeader` /
// per-index totals) render per-index results without branching.
//
// Hit shape is the original Redux record spread + an `objectID` key, plus the
// `__minisearch*` fields describing which profile produced it and why.

// ─── Tokenization & analyser ───────────────────────────────────────────────

// `tokenize` is the shared Unicode-aware tokenizer (diacritic folding + CJK /
// no-space-script segmentation). MiniSearch applies it to BOTH documents and
// queries, so both sides stay symmetric. Because folding already happens
// inside `tokenize`, `processTerm` only needs to drop empty tokens.
function processTerm(term) {
  if (typeof term !== 'string') return false
  return term.length > 0 ? term : false
}

// ─── Search profiles ───────────────────────────────────────────────────────
//
// Every profile runs over the SAME indices. MiniSearch accepts a full set of
// search options per `search()` call, so each extra profile costs one in-memory
// BM25 pass over data that is already loaded — no second index, no extra memory
// for documents.
//
// One profile is DISPLAYED (production). The SHADOW profiles run on every query
// too, are never shown, and are reported beside the displayed one — so each
// experiment is compared against production on the same query, library, user
// and moment rather than week-over-week. See SEARCH_QUALITY_ANALYTICS.md.
//
// A profile's id IS its version label. Never change the values behind an
// existing id: add a new id instead, or rows recorded under different
// configurations get pooled in Redshift.
//
//   Add an experiment   add a PROFILES entry, list it in SHADOW_PROFILES
//   Promote a profile   point DISPLAY_PROFILE at it
//
// The history of every retired profile (baseline, tuned-v1…v6) lives in
// SEARCH_QUALITY_ANALYTICS.md §0.

// Non-global so `.test()` stays stateless.
const HAS_DIGIT = /\d/

// ─── Tuned search options ──────────────────────────────────────────────────
//
// Mirrors Algolia's actual typo configuration: `minWordSizefor1Typo: 4`,
// `minWordSizefor2Typos: 8`, and prefix on the last token only.
//
// `weights` lowers the score contribution of fuzzy and prefix matches relative
// to exact ones (MiniSearch defaults are 0.45 / 0.375), so an exact match on a
// less-boosted field still outranks an approximate match on a boosted one.
//
// ── v2, from three days of tuned-v1 telemetry ──
//
// `prefix` used to carry a `term.length >= 3` floor as well. Algolia's
// `prefixLast` has no minimum token length, so the floor made this profile
// stricter than the engine the comment above claims to mirror, and Q30 sized
// the cost: on one-character queries the tuned arm returned nothing at all on
// 43.7% of the queries where Algolia found something, against baseline's 6.8%;
// at two characters 41.1% against 0.7%. The two arms converge from three
// characters up — exactly where the floor stopped applying. Removed.
//
// `index === terms.length - 1` stays: that is the part that actually mirrors
// `prefixLast`, and it is what fixed the baseline's `M` → 42-distinct-titles
// behaviour. `!HAS_DIGIT` stays too — it belongs to the digit knob, which is
// the one change v1 got right (excess per query 3.10 → 1.23, precision +0.056).
const TUNED_SEARCH_OPTIONS = {
  combineWith: 'AND',
  prefix: (term, index, terms) => (
    index === terms.length - 1 && !HAS_DIGIT.test(term)
  ),
  // v6: absolute edit distances, not fractions. MiniSearch reads `fuzzy >= 1`
  // as a maximum edit distance and a fraction as `round(length * fuzzy)`, so
  // the old 0.25 ladder allowed THREE edits at ten characters and five at
  // twenty, where Algolia's minWordSizefor2Typos caps at two however long the
  // word. These two integers reproduce Algolia's ladder exactly. See §0f.4.
  //
  // v6 also drops the `HAS_DIGIT` guard. The comment justifying it claimed
  // Algolia treats numbers as exact; it does not — `allowTyposOnNumericTokens`
  // defaults to true and is unset in production, so Algolia allows typos on
  // digit tokens and we did not. The rule was credited with v1's excess win,
  // and the likely mechanism is that Algolia matches those hits and RANKS them
  // below the cap. Restoring it is a direct test of whether the comparator can
  // do that job instead of the matcher. Watch Q22 `alnum_code` /
  // `contains_digit` and Q28. See §0f.5.
  fuzzy: (term) => {
    if (term.length >= 8) return 2
    if (term.length >= 4) return 1
    return false
  },
  weights: { fuzzy: 0.2, prefix: 0.3 },
  // Query side only — documents keep their n-grams. See tokenizeQuery.
  tokenize: tokenizeQuery,
}

// ─── Re-rank profile (tuned-v9) ────────────────────────────────────────────
//
// Through v7 this lane held the matcher identical to TUNED and varied only the
// SORT, which is what isolated ranking. That question is answered: the
// comparator moves rho by +0.003 at one word and +0.004 at three, and its only
// real effect is +0.067 on two-word queries. So from v8 the comparator runs in
// both lanes as a constant and the matcher is what varies instead.
//
// tuned-v9 restores MiniSearch's DEFAULT term weights and changes nothing else.
// This is the second suspect for the single-word ranking loss.
//
// `weights` scales the score of a term matched approximately against one
// matched exactly. At one word the query has a single token and `prefixLast`
// makes it a prefix term, so every hit in the index matches through this path
// and the weight decides how hard an exact-token hit separates from a
// prefix-only hit: 0.3 here against MiniSearch's 0.375.
//
// The reason to suspect it is that the separation it creates is the WRONG
// separation. Algolia's `exact` criterion on a single-word query fires only on
// a whole-ATTRIBUTE match (§0f.1b) — a set titled "Fractions" — and never on a
// whole-token match inside a longer title. Sharpening exact-token preference
// therefore pulls away from Algolia's order rather than towards it, and the
// tuned arm sharpens it more than the baseline does.
const TUNED_V9_SEARCH_OPTIONS = (() => {
  const { weights, ...rest } = TUNED_SEARCH_OPTIONS
  return rest
})()

// Which fields each profile actually searches, per index.
//
// Indexing a field and searching it are separate decisions, so profiles can
// differ in what they search without a second index:
//
//   BASELINE  — the original field set, from the retired baseline profile.
//               `sets_questions` leaves out `description`; tuned-v8 reuses
//               that to test whether `description` hurts ranking.
//   TUNED     — mirrors Algolia's `attributesToIndex`, per index:
//                 repos          ["name", "description"]  — author fields are
//                                NOT searchable in Algolia, so searching them
//                                is pure local-only excess (§0f.3)
//                 folders        unset — everything (only `name` exists anyway)
//                 setsQuestions  unset — everything, so `description` counts
//                                (§0f.1a)
//
// MiniSearch accepts `fields` as a SEARCH option, so one index serves both.
const BASELINE_FIELDS = {
  repos: ['name', 'description', 'authorFirstName', 'authorLastName'],
  folders: ['name'],
  setsQuestions: ['name', 'questionBodies', 'questionChoices', 'body', 'choices'],
}

const TUNED_FIELDS = {
  repos: ['name', 'description'],
  folders: ['name'],
  setsQuestions: ['name', 'description', 'questionBodies', 'questionChoices', 'body', 'choices'],
}

// tuned-v8 strips `description` back out of `sets_questions` and restores the
// baseline boosts for that index, keeping every other v6/v7 change. It is the
// first of the two suspects for the single-word ranking loss: `description`
// entered the index at boost 3 against a title at 4, and Algolia ranks the two
// EQUALLY on this index (no attribute priority, §0f.1) while resolving
// single-word queries through `exactOnSingleWordQuery: "attribute"` — a
// whole-attribute test our BM25 score has no equivalent of. The repos field
// narrowing stays: it is a separate, measured win (§0f.3) and cannot affect
// sets_questions.
const TUNED_V8_FIELDS = {
  repos: TUNED_FIELDS.repos,
  folders: TUNED_FIELDS.folders,
  setsQuestions: BASELINE_FIELDS.setsQuestions,
}

const TUNED_SCORE_FLOOR_RATIO = 0.25

// ─── Per-domain index builders ─────────────────────────────────────────────
//
// Each builder produces an independent MiniSearch instance with field
// boosts that mirror `TEMP_LOCAL_SEARCH_INDEXES.md`. The `_kind` field
// labels every document so the unified `sets_questions` index can keep
// the set/question discriminator implicit (matches Algolia's behaviour).
// `_original` carries the source Redux record back into the hit; this
// trades memory for code simplicity and avoids a parallel id→record map.

function notArchived(item) {
  return item && !item.archived && !item.parentArchived
}

function buildRepoDocs(repos) {
  return repos.filter(notArchived).map((repo) => {
    const author = repo.author || {}
    return {
      msId: `repo-${repo.id}`,
      name: repo.name,
      description: repo.description,
      authorFirstName: author.firstName,
      authorLastName: author.lastName,
      _kind: 'repo',
      _original: repo,
    }
  })
}

function buildFolderDocs(folders) {
  return folders.filter(notArchived).map((folder) => ({
    msId: `folder-${folder.id}`,
    name: folder.name,
    _kind: 'folder',
    _original: folder,
  }))
}

function buildSetDocs(sets) {
  return sets.filter(notArchived).map((set) => ({
    msId: `set-${set.id}`,
    name: set.name,
    // Indexed for every profile, searched only by the profiles whose field set
    // includes it — see PROFILES.
    description: set.description,
    // Derived through documentFields so the comparator can recover exactly this
    // text at query time to locate WHERE a term matched. See documentFields.js.
    questionBodies: setQuestionBodies(set),
    questionChoices: setQuestionChoices(set),
    _kind: 'set',
    _original: set,
  }))
}

function buildQuestionDocs(questions) {
  return questions.filter(notArchived).map((question) => ({
    msId: `question-${question.id}`,
    body: question.body,
    choices: questionChoiceBodies(question),
    _kind: 'question',
    _original: question,
  }))
}

// ─── Per-profile field boosts ──────────────────────────────────────────────
//
// Only `setsQuestions` differs between the profiles, deliberately: it is the
// index carrying nearly all the traffic, and keeping repos/folders identical
// means any movement there cannot be attributed to a boost change.
//
// BASELINE_BOOSTS are the retired baseline profile's values, still used by
// tuned-v8 and as the index constructors' defaults. The baseline weights body text (`questionBodies: 2` against `name: 3`) high
// enough that a word appearing in ANY question of a set matches the set — the
// reason a query for `Proteins` returns "An alpha particle consists of …", and
// why `math`, `name` and `reading` each return 20+ unrelated sets. The tuned
// profile widens the gap between the title and the body fields instead of
// removing body matching, so a title match wins whenever one exists.
//
// ── v2, from three days of tuned-v1 telemetry ──
//
// v1 widened that gap too far: `name: 6` against `questionBodies: 1` puts a
// body-only match at ~1/6 of a title match, under TUNED_SCORE_FLOOR_RATIO, so
// the floor deleted it before it could be ranked. Q31 sized the damage — 93.6%
// of the documents the tuned profile lost that the baseline found (1,719 of
// 1,836, across 995 distinct titles) had titles that never contained the query
// at all. They were reachable only through question text.
//
// v2 keeps a title-over-body preference but at 2:1 rather than 6:1, so a
// body-only match lands at ~0.5 of a title match and clears the 0.25 floor with
// room to spare. Baseline's ratio, for reference, is 1.5:1.
const BASELINE_BOOSTS = {
  repos: {
    name: 3, description: 1, authorFirstName: 1, authorLastName: 1,
  },
  folders: { name: 1 },
  setsQuestions: {
    name: 3, questionBodies: 2, questionChoices: 1, body: 3, choices: 1,
  },
}

const TUNED_BOOSTS = {
  repos: BASELINE_BOOSTS.repos,
  folders: BASELINE_BOOSTS.folders,
  setsQuestions: {
    // `description` is about the set as a whole, like `name`, but is shorter and
    // less deliberate — so it sits between the title and the question text.
    name: 4, description: 3, questionBodies: 2, questionChoices: 1, body: 4, choices: 1,
  },
}

// Paired with TUNED_V8_FIELDS: reverting the field without reverting the boost
// would leave `name` at 4 and `body` at 4, which is half the change and would
// make the lane un-attributable.
const TUNED_V8_BOOSTS = {
  repos: TUNED_BOOSTS.repos,
  folders: TUNED_BOOSTS.folders,
  setsQuestions: BASELINE_BOOSTS.setsQuestions,
}

const REPOS_OPTIONS = {
  idField: 'msId',
  fields: ['name', 'description', 'authorFirstName', 'authorLastName'],
  storeFields: ['_kind', '_original'],
  tokenize,
  processTerm,
  searchOptions: { ...TUNED_SEARCH_OPTIONS, boost: TUNED_BOOSTS.repos },
}

const FOLDERS_OPTIONS = {
  idField: 'msId',
  fields: ['name'],
  storeFields: ['_kind', '_original'],
  tokenize,
  processTerm,
  searchOptions: { ...TUNED_SEARCH_OPTIONS, boost: TUNED_BOOSTS.folders },
}

const SETS_QUESTIONS_OPTIONS = {
  idField: 'msId',
  fields: [
    'name', 'description', 'questionBodies', 'questionChoices',
    'body', 'choices',
  ],
  storeFields: ['_kind', '_original'],
  tokenize,
  processTerm,
  searchOptions: { ...TUNED_SEARCH_OPTIONS, boost: TUNED_BOOSTS.setsQuestions },
}

// ─── Memoisation cache ─────────────────────────────────────────────────────
//
// Keyed on the input list reference. Redux replaces the array reference
// whenever any item changes; the cache returns the same MiniSearch instance
// until that happens.

const reposCache = new WeakMap()
const foldersCache = new WeakMap()
const setsQuestionsCache = new WeakMap()

function getRepoIndex(repos) {
  const list = Array.isArray(repos) ? repos : []
  const cached = reposCache.get(list)
  if (cached) return cached
  const ms = new MiniSearch(REPOS_OPTIONS)
  ms.addAll(buildRepoDocs(list))
  reposCache.set(list, ms)
  return ms
}

function getFolderIndex(folders) {
  const list = Array.isArray(folders) ? folders : []
  const cached = foldersCache.get(list)
  if (cached) return cached
  const ms = new MiniSearch(FOLDERS_OPTIONS)
  ms.addAll(buildFolderDocs(list))
  foldersCache.set(list, ms)
  return ms
}

function getSetsQuestionsIndex(sets, questions) {
  const setsList = Array.isArray(sets) ? sets : []
  const questionsList = Array.isArray(questions) ? questions : []
  const cached = setsQuestionsCache.get(setsList)
  if (cached && cached.questions === questionsList) return cached.ms
  const ms = new MiniSearch(SETS_QUESTIONS_OPTIONS)
  ms.addAll([...buildSetDocs(setsList), ...buildQuestionDocs(questionsList)])
  setsQuestionsCache.set(setsList, { questions: questionsList, ms })
  return ms
}

/**
 * Build per-domain MiniSearch indices from Redux collections.
 *
 * @returns {{ repos: MiniSearch, folders: MiniSearch, setsQuestions: MiniSearch }}
 */
export function buildMiniSearchIndices({
  sets, questions, folders, repos,
}) {
  return {
    repos: getRepoIndex(repos),
    folders: getFolderIndex(folders),
    setsQuestions: getSetsQuestionsIndex(sets, questions),
  }
}

/**
 * Per-index document counts for a built index set.
 *
 * Reported alongside every results report. MiniSearch only indexes what Redux
 * has loaded, so this, beside the expected counts, is what shows whether the
 * library was complete when a query ran.
 *
 * @param {ReturnType<typeof buildMiniSearchIndices>} indices
 * @returns {{ repos: number, folders: number, sets_questions: number }}
 */
export function miniSearchIndexSizes(indices) {
  const count = (ms) => ((ms && typeof ms.documentCount === 'number') ? ms.documentCount : 0)
  const set = indices || {}
  return {
    repos: count(set.repos),
    folders: count(set.folders),
    // eslint-disable-next-line camelcase
    sets_questions: count(set.setsQuestions),
  }
}

// ─── Profile registry ──────────────────────────────────────────────────────
//
//   tuned-v7   production. Algolia's typo ladder and prefixLast, the repos field
//              narrowing, `description` searched on sets_questions, the score
//              floor, and the Algolia-style comparator. See §0g.
//   tuned-v8   tuned-v7 with the `sets_questions` field + boost change reverted.
//   tuned-v9   tuned-v7 with MiniSearch's default term weights.
//
// v8 and v9 each strip one suspect for the single-word ranking loss from the v7
// configuration and keep everything else, so a difference between either lane
// and production is that one knob and nothing else. See §0h.
//
// `scoreFloor` trims a junk tail when the surface cap binds (see
// applyScoreFloor). `rerank` orders the candidate set with the Algolia-style
// comparator before the cap is applied (see applyRerank).
export const PROFILES = {
  'tuned-v7': {
    options: TUNED_SEARCH_OPTIONS,
    fields: TUNED_FIELDS,
    boosts: TUNED_BOOSTS,
    scoreFloor: true,
    rerank: true,
  },
  'tuned-v8': {
    options: TUNED_SEARCH_OPTIONS,
    fields: TUNED_V8_FIELDS,
    boosts: TUNED_V8_BOOSTS,
    scoreFloor: true,
    rerank: true,
  },
  'tuned-v9': {
    options: TUNED_V9_SEARCH_OPTIONS,
    fields: TUNED_FIELDS,
    boosts: TUNED_BOOSTS,
    scoreFloor: true,
    rerank: true,
  },
}

// The profile users see. Changing it changes production search.
export const DISPLAY_PROFILE = 'tuned-v7'

// Experiments. Each runs on every query beside DISPLAY_PROFILE, is never shown,
// and is reported against it. Every lane adds to the report payload, so keep
// this short and retire a lane once its question is answered.
export const SHADOW_PROFILES = ['tuned-v8', 'tuned-v9']

// An unknown id is a programming error, so it fails loudly in development. In
// production it degrades to the displayed profile rather than breaking search.
function resolveProfile(profile) {
  const entry = PROFILES[profile]
  if (entry) return { id: profile, ...entry }
  if (process.env.NODE_ENV !== 'production') {
    throw new Error(`Unknown MiniSearch profile: ${profile}`)
  }
  return { id: DISPLAY_PROFILE, ...PROFILES[DISPLAY_PROFILE] }
}

// ─── Hit projection ────────────────────────────────────────────────────────

function toHit(result, profileId) {
  const original = result._original || {}
  return {
    ...original,
    objectID: original.id,
    __minisearchOrigin: true,
    __minisearchProfile: profileId,
    __minisearchScore: result.score,
    // MiniSearch returns `terms: ['math']` and `match: { math: ['name'] }`.
    // Both are kept on the hit so the search-tuning sandbox can show which
    // query term matched which field.
    __minisearchTerms: result.terms,
    __minisearchMatch: result.match,
  }
}

const NOOP = () => {}

// Per-index hit cap applied when the caller does not pass one. Exported so the
// orchestrator can report the cap actually in force alongside each results
// report — a row that returned exactly the cap is measuring the cap.
export const DEFAULT_LIMIT_PER_INDEX = 10

function emptyIndexResult() {
  return {
    hits: [], hitCount: 0, hasMore: false, loadMore: NOOP, rankedIds: [],
  }
}

function profileSearchOptions(profile, indexKey) {
  return {
    ...profile.options,
    fields: profile.fields[indexKey],
    boost: profile.boosts[indexKey],
  }
}

// MiniSearch returns results already sorted by descending score, so the top hit
// sets the bar for the rest.
//
// v3: the floor only runs when the slice actually binds. It exists to stop a
// long junk tail padding out a fixed-size slice; when there are fewer matches
// than the caller asked for there is no tail to cut. Under v2 it ran
// unconditionally and truncated wide surfaces badly — on sectionHome it reduced
// 200 available slots to 19 returned where Algolia found 47, and 676 of 683
// remaining losses happened nowhere near the cap. See §4g.
function applyScoreFloor(results, profile, limit) {
  if (!profile.scoreFloor || results.length === 0) return results
  if (typeof limit !== 'number' || results.length <= limit) return results
  const floor = results[0].score * TUNED_SCORE_FLOOR_RATIO
  const kept = results.filter((r) => r.score >= floor)
  // The floor reorders and trims the tail; it must never leave the caller with
  // fewer hits than it asked for.
  return kept.length >= limit ? kept : results.slice(0, limit)
}

// Re-order the candidate set the way Algolia would. Runs BEFORE the cap is
// applied, which is the entire mechanism: on a capped surface the sort decides
// which documents survive the slice, so this changes membership and not just
// presentation. See algoliaComparator.js.
function applyRerank(results, query, profile, indexKey) {
  if (!profile.rerank) return results
  return applyAlgoliaRerank(results, query, indexKey)
}

function runIndex(ms, query, limit, profile, indexKey) {
  if (!query || !ms) return emptyIndexResult()
  const found = ms.search(query, profileSearchOptions(profile, indexKey))
  const floored = applyScoreFloor(found, profile, limit)
  const all = applyRerank(floored, query, profile, indexKey)
  const hitCount = all.length
  const capped = (typeof limit === 'number' && limit > 0) ? all.slice(0, limit) : all
  return {
    hits: capped.map((result) => toHit(result, profile.id)),
    hitCount,
    // Everything is already in memory, so "more" simply means the cap cut the
    // list short. The provider supplies the `loadMore` that raises the cap —
    // this module has no state to page through.
    hasMore: capped.length < hitCount,
    loadMore: NOOP,
    // The full ordered id list, beyond the cap. Lets a click on any displayed
    // hit be located in every profile's ranking, including a shadow lane that
    // ranked it below its own cap.
    rankedIds: all.map((result) => (result._original || {}).id),
  }
}

// `limitPerIndex` is either one number for every index, or a per-index map when
// the caller pages indexes independently (Show more raises one index's cap
// without disturbing the others).
function resolveLimit(limitPerIndex, resultKey) {
  if (limitPerIndex && typeof limitPerIndex === 'object') {
    const limit = limitPerIndex[resultKey]
    return typeof limit === 'number' ? limit : DEFAULT_LIMIT_PER_INDEX
  }
  return limitPerIndex
}

/**
 * Run a query against every MiniSearch index under one profile and return
 * per-index results.
 *
 * `hitCount` is the true uncapped match count while `hits` is capped at
 * `limitPerIndex`; `rankedIds` holds every matching id in order.
 *
 * @param {ReturnType<typeof buildMiniSearchIndices>} indices
 * @param {string} query
 * @param {number|{repos: number, folders: number, sets_questions: number}} [limitPerIndex=10]
 * @param {string} [profile=DISPLAY_PROFILE] a key of PROFILES
 */
export function searchPerMiniSearchIndex(
  indices,
  query,
  limitPerIndex = DEFAULT_LIMIT_PER_INDEX,
  profile = DISPLAY_PROFILE,
) {
  const resolved = resolveProfile(profile)
  const limitFor = (resultKey) => resolveLimit(limitPerIndex, resultKey)
  return {
    repos: runIndex(indices.repos, query, limitFor('repos'), resolved, 'repos'),
    folders: runIndex(indices.folders, query, limitFor('folders'), resolved, 'folders'),
    // eslint-disable-next-line camelcase
    sets_questions: runIndex(
      indices.setsQuestions,
      query,
      limitFor('sets_questions'),
      resolved,
      'setsQuestions',
    ),
  }
}
